import React, { useState } from 'react';
import api from '../lib/api';
import { motion } from 'framer-motion';

const MusicRequestForm = ({ onClose, onSuccess }) => {
  const [requestType, setRequestType] = useState('song');
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [genre, setGenre] = useState('');
  const [customGenre, setCustomGenre] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (requestType === 'song' && !title && !artist) {
      alert('Por favor ingresa al menos el titulo o artista');
      return;
    }
    const finalGenre = genre === 'Otro' ? customGenre : genre;
    if (requestType === 'genre' && !finalGenre) {
      alert('Por favor selecciona o escribe un genero');
      return;
    }
    try {
      setSubmitting(true);
      const response = await api.post(`/requests`, {
        request_type: requestType,
        title: title || null,
        artist: artist || null,
        genre: finalGenre || null,
        description: description || null
      }, { withCredentials: true });
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          if (onSuccess) onSuccess();
          if (onClose) onClose();
        }, 2000);
      }
    } catch (error) {
      alert('Error al enviar solicitud: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const genres = [
    'Reggaeton', 'Dembow', 'Latin Pop', 'Bachata', 'Salsa',
    'Merengue', 'Cumbia', 'Hip Hop', 'House', 'EDM',
    'R&B', 'Trap Latino', 'Reggae', 'Rock en Espanol', 'Otro'
  ];

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#0A0A0A] border border-[#1E1E1E] rounded-2xl p-8 max-w-md w-full text-center shadow-2xl shadow-black/50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-14 h-14 rounded-2xl bg-[#00D9FF]/10 border border-[#00D9FF]/20 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-[#00D9FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Solicitud Enviada</h3>
          <p className="text-[#555] text-sm">Te notificaremos cuando tu musica este disponible.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="bg-[#0A0A0A] border border-[#1E1E1E] rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl shadow-black/50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#1E1E1E] flex items-center justify-between sticky top-0 bg-[#0A0A0A] z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#00D9FF]/10 border border-[#00D9FF]/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-[#00D9FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Solicitar Musica</h2>
              <p className="text-xs text-[#555]">Pidenos lo que necesites</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-request-form"
            className="w-8 h-8 rounded-lg bg-[#141414] border border-[#262626] flex items-center justify-center text-[#555] hover:text-white hover:border-[#444] transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Type Toggle */}
          <div>
            <label className="block text-xs font-medium text-[#555] mb-2 uppercase tracking-wider">Tipo de solicitud</label>
            <div className="flex gap-2 bg-[#141414] p-1 rounded-xl border border-[#1E1E1E]">
              <button
                type="button"
                onClick={() => setRequestType('song')}
                data-testid="request-type-song"
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                  requestType === 'song'
                    ? 'bg-[#00D9FF] text-black shadow-lg shadow-[#00D9FF]/10'
                    : 'text-[#555] hover:text-white'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"/>
                </svg>
                Cancion
              </button>
              <button
                type="button"
                onClick={() => setRequestType('genre')}
                data-testid="request-type-genre"
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                  requestType === 'genre'
                    ? 'bg-[#00D9FF] text-white shadow-lg shadow-[#00D9FF]/10'
                    : 'text-[#555] hover:text-white'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                </svg>
                Genero
              </button>
            </div>
          </div>

          {/* Song Fields */}
          {requestType === 'song' && (
            <>
              <div>
                <label className="block text-xs font-medium text-[#555] mb-2 uppercase tracking-wider">Titulo de la cancion</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ej: Titi Me Pregunto"
                  data-testid="request-title-input"
                  className="w-full px-4 py-3 bg-[#141414] border border-[#1E1E1E] text-white text-sm rounded-xl placeholder:text-[#333] focus:outline-none focus:border-[#00D9FF]/40 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#555] mb-2 uppercase tracking-wider">Artista</label>
                <input
                  type="text"
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  placeholder="Ej: Bad Bunny"
                  data-testid="request-artist-input"
                  className="w-full px-4 py-3 bg-[#141414] border border-[#1E1E1E] text-white text-sm rounded-xl placeholder:text-[#333] focus:outline-none focus:border-[#00D9FF]/40 transition-colors"
                />
              </div>
            </>
          )}

          {/* Genre Field */}
          {requestType === 'genre' && (
            <div>
              <label className="block text-xs font-medium text-[#555] mb-2 uppercase tracking-wider">Genero</label>
              <div className="flex flex-wrap gap-2">
                {genres.map(g => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGenre(g)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                      genre === g
                        ? 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/40'
                        : 'bg-[#141414] text-[#555] border-[#1E1E1E] hover:text-white hover:border-[#333]'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
              {genre === 'Otro' && (
                <input
                  type="text"
                  value={customGenre}
                  onChange={(e) => setCustomGenre(e.target.value)}
                  placeholder="Escribe el genero"
                  className="w-full mt-3 px-4 py-3 bg-[#141414] border border-[#1E1E1E] text-white text-sm rounded-xl placeholder:text-[#333] focus:outline-none focus:border-[#00D9FF]/40 transition-colors"
                />
              )}
            </div>
          )}

          {/* Details */}
          <div>
            <label className="block text-xs font-medium text-[#555] mb-2 uppercase tracking-wider">Detalles (opcional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ej: Version clean, remix especifico, etc."
              rows={3}
              data-testid="request-details-input"
              className="w-full px-4 py-3 bg-[#141414] border border-[#1E1E1E] text-white text-sm rounded-xl placeholder:text-[#333] focus:outline-none focus:border-[#00D9FF]/40 transition-colors resize-none"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-request-btn"
            className="w-full py-3.5 bg-[#00D9FF] hover:bg-[#00C4E8] text-black font-bold text-sm rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-black/30 border-t-black animate-spin"></div>
                Enviando...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                </svg>
                Enviar Solicitud
              </>
            )}
          </button>

          <p className="text-center text-[#333] text-xs">
            Revisamos las solicitudes y agregamos musica nueva cada semana
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default MusicRequestForm;
